import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Check, Globe, MapPin, Zap, Building2 } from "lucide-react";
import { PageHeader, SectionTitle } from "@/components/site/kit";
import { Reveal } from "@/components/motion/primitives";
import SEO, { breadcrumbSchema } from "@/components/site/SEO";
import { MARKETS, getMarket, getCombo, combosForCity, combosForIndustry, COUNTRY_LIST, INDUSTRY_LIST, CITY_GROUPS, SERVICES_OFFERED } from "@/lib/markets";
import { getPricingConfig } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";

const faqSchema = (faqs) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
});

function MarketCTA({ label }) {
  return (
    <section className="py-20 md:py-28 bg-primary text-primary-foreground" data-testid="market-cta">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10 text-center">
        <h2 className="font-display text-4xl md:text-6xl leading-[0.95] max-w-3xl mx-auto">Ready to build a better website?</h2>
        <p className="mt-6 text-primary-foreground/85 text-lg max-w-xl mx-auto">
          Get an instant estimate for your {label} project — 60 seconds, no sales call.
        </p>
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link to="/pricing" data-testid="market-quote-cta" className="inline-flex items-center gap-2 bg-background text-primary px-8 py-4 font-semibold hover:bg-background/90 active:scale-95 transition">
            Get My Instant Quote <Zap className="w-5 h-5" />
          </Link>
          <Link to="/start-project" data-testid="market-start-cta" className="inline-flex items-center gap-2 border border-primary-foreground/40 px-8 py-4 font-semibold hover:bg-primary-foreground/10 active:scale-95 transition">
            Start My Project <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/** Hub page: /web-design — index of every market page (internal linking). */
function Hub() {
  return (
    <div data-testid="web-design-hub">
      <SEO
        title="Web Design Services Worldwide — Countries, Cities & Industries | Julius Mason"
        description="Professional web design for businesses worldwide. Explore web design services by country, city, and industry — from New York to London, Toronto to Sydney."
        path="/web-design"
        lang="en"
        keywords="web design services, website design worldwide, web designer for small business"
        schema={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Web Design", path: "/web-design" }])}
      />
      <PageHeader
        chapter="Web Design / Worldwide"
        titleLines={["Web design for", "your market."]}
        description="Professional websites for businesses everywhere — explore services by country, city, or industry. Every market page includes real pricing, services, and FAQs."
      />

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 space-y-20">
          <div>
            <SectionTitle overline="By Country" title="Serving businesses worldwide." />
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {COUNTRY_LIST.map((c, i) => (
                <Reveal key={c.slug} delay={i * 0.03}>
                  <Link to={`/web-design/${c.slug}`} data-testid={`hub-country-${c.slug}`} className="group flex items-center justify-between border border-border bg-card p-5 hover-lift">
                    <span className="flex items-center gap-3"><Globe className="w-5 h-5 text-primary" /> <span className="font-display text-xl">Web Design in {c.name}</span></span>
                    <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>

          <div>
            <SectionTitle overline="By Industry" title="Built for your kind of business." />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {INDUSTRY_LIST.map((ind, i) => (
                <Reveal key={ind.slug} delay={i * 0.03}>
                  <Link to={`/web-design/${ind.slug}`} data-testid={`hub-industry-${ind.slug}`} className="group flex items-center justify-between border border-border bg-card p-5 hover-lift">
                    <span className="flex items-center gap-3"><Building2 className="w-5 h-5 text-primary" /> <span className="font-display text-xl">Web Design for {ind.name}</span></span>
                    <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>

          {CITY_GROUPS.map((g) => (
            <div key={g.countrySlug}>
              <SectionTitle overline={`${g.country} — By City`} title={`${g.country} markets.`} />
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                {g.cities.map((c) => (
                  <Link key={c.slug} to={`/web-design/${c.slug}`} data-testid={`hub-city-${c.slug}`}
                    className="group flex items-center gap-2.5 border border-border px-4 py-3 text-sm hover:border-primary hover:text-primary transition-colors">
                    <MapPin className="w-4 h-4 text-primary flex-shrink-0" /> Web Design in {c.name}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>
      <MarketCTA label="web design" />
    </div>
  );
}

/** Market page: /web-design/:slug */
function Market({ market }) {
  const [cfg, setCfg] = useState(null);
  const { fmt } = useCurrency();
  useEffect(() => { getPricingConfig().then(setCfg).catch(() => {}); }, []);

  const isIndustry = market.kind === "industry";
  const place = market.name;
  const h1 = isIndustry ? `Web Design for ${place}` : `Web Design for Businesses in ${place}`;
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: h1,
    provider: { "@type": "Person", name: "Julius Mason", url: "https://juliusmason.com" },
    areaServed: isIndustry ? "Worldwide" : place,
    description: market.description,
    offers: { "@type": "Offer", priceCurrency: "USD", price: "499", description: "Websites starting at $499" },
  };
  const related = (market.related || []).map(getMarket).filter(Boolean);

  return (
    <div data-testid={`market-${market.slug}`}>
      <SEO
        title={`${market.title} | Julius Mason`}
        description={market.description}
        path={`/web-design/${market.slug}`}
        lang="en"
        keywords={`web design ${place}, website designer ${place}, small business website ${place}`}
        schema={[
          serviceSchema,
          faqSchema(market.faqs),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Web Design", path: "/web-design" },
            { name: market.name, path: `/web-design/${market.slug}` },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="pt-36 md:pt-44 pb-16 md:pb-20 border-b border-border">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <nav className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6" aria-label="Breadcrumb" data-testid="market-breadcrumb">
            <Link to="/" className="hover:text-primary">Home</Link> / <Link to="/web-design" className="hover:text-primary">Web Design</Link> / <span className="text-foreground">{market.name}</span>
          </nav>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-4xl" data-testid="market-h1">{h1}</h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">{market.intro}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/pricing" data-testid="market-hero-quote" className="inline-flex items-center gap-2 bg-primary text-white px-7 py-3.5 font-semibold hover:bg-primary/90 active:scale-95 transition">
              Get My Instant Quote <Zap className="w-4 h-4" />
            </Link>
            <Link to="/clients" className="inline-flex items-center gap-2 border border-border px-7 py-3.5 font-medium hover:border-primary hover:text-primary active:scale-95 transition">
              Programs I Support <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="mt-5 font-mono text-xs uppercase tracking-widest text-muted-foreground">
            Remote service · Available {isIndustry ? "worldwide" : `everywhere ${market.kind === "country" ? "across" : "across"} ${place} & beyond`} · Transparent pricing
          </p>
        </div>
      </section>

      {/* What businesses here need */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid lg:grid-cols-2 gap-14">
          <div>
            <SectionTitle overline="The Local Reality" title={isIndustry ? `What ${place.toLowerCase()} need from a website.` : `What businesses in ${place} need online.`} />
            <ul className="space-y-4 -mt-4">
              {market.needs.map((n, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <li className="flex gap-3 text-muted-foreground leading-relaxed" data-testid={`market-need-${i}`}>
                    <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" /> {n}
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
          <div>
            <SectionTitle overline="Services Offered" title="What I can build for you." />
            <ul className="space-y-4 -mt-4">
              {SERVICES_OFFERED.map((s, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <li className="flex gap-3 text-muted-foreground leading-relaxed">
                    <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" /> {s}
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Typical projects + pricing */}
      <section className="py-16 md:py-24 border-y border-border bg-secondary/20">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid lg:grid-cols-2 gap-14">
          <div>
            <SectionTitle overline="Typical Projects" title={isIndustry ? `Common ${place.toLowerCase()} projects.` : `Common projects in ${place}.`} />
            <div className="space-y-3 -mt-4">
              {market.projects.map((p, i) => (
                <div key={i} className="border border-border bg-card px-5 py-4 font-medium" data-testid={`market-project-${i}`}>{p}</div>
              ))}
            </div>
          </div>
          <div>
            <SectionTitle overline="Transparent Pricing" title="Starting points." />
            {cfg ? (
              <div className="space-y-3 -mt-4" data-testid="market-pricing">
                {cfg.packages.map((p) => (
                  <div key={p.id} className="flex items-center justify-between border border-border bg-card px-5 py-4">
                    <span className="font-medium">{p.name}</span>
                    <span className="font-mono text-primary">{p.custom ? `${fmt(p.price_from)}+` : `from ${fmt(p.price_from)}`}</span>
                  </div>
                ))}
                <p className="text-xs text-muted-foreground leading-relaxed pt-2">
                  Estimates in your currency are indicative — every project is confirmed with a written proposal before work begins.
                </p>
                <Link to="/pricing" className="inline-flex items-center gap-2 text-primary font-medium link-underline" data-testid="market-pricing-link">
                  Full pricing &amp; care plans <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            ) : <div className="h-32" />}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <SectionTitle overline="FAQ" title="Good questions." />
          </div>
          <div className="lg:col-span-8">
            {market.faqs.map((f, i) => (
              <details key={i} className="group border-b border-border py-5" data-testid={`market-faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer font-medium text-lg list-none">
                  {f.q}
                  <ArrowRight className="w-4 h-4 text-primary rotate-90 group-open:-rotate-90 transition-transform flex-shrink-0 ml-4" />
                </summary>
                <p className="mt-4 text-muted-foreground leading-relaxed pr-8">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related markets + combos */}
      {(() => {
        const comboLinks = market.kind === "city"
          ? combosForCity(market.slug).map((i) => ({ to: `/web-design/${market.slug}/${i}`, label: `${getMarket(i)?.name} in ${market.name}` }))
          : market.kind === "industry"
            ? combosForIndustry(market.slug).map((c) => ({ to: `/web-design/${c}/${market.slug}`, label: `${market.name} in ${getMarket(c)?.name}` }))
            : [];
        const related = (market.related || []).map(getMarket).filter(Boolean);
        if (!comboLinks.length && !related.length) return null;
        return (
          <section className="py-16 border-t border-border">
            <div className="mx-auto max-w-[1600px] px-5 md:px-10 space-y-8">
              {comboLinks.length > 0 && (
                <div>
                  <p className="overline text-primary mb-6">{market.kind === "city" ? `By industry in ${market.name}` : `${market.name} — by city`}</p>
                  <div className="flex flex-wrap gap-3" data-testid="market-combos">
                    {comboLinks.map((l) => (
                      <Link key={l.to} to={l.to} data-testid={`combo-${l.to.split("/").slice(2).join("-")}`}
                        className="border border-primary/40 text-primary px-4 py-2.5 text-sm hover:bg-primary/5 transition-colors">
                        Web Design for {l.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
              {related.length > 0 && (
                <div>
                  <p className="overline text-primary mb-6">Also serving</p>
                  <div className="flex flex-wrap gap-3" data-testid="market-related">
                    {related.map((r) => (
                      <Link key={r.slug} to={`/web-design/${r.slug}`} data-testid={`market-related-${r.slug}`}
                        className="border border-border px-4 py-2.5 text-sm hover:border-primary hover:text-primary transition-colors">
                        {r.kind === "industry" ? `Web Design for ${r.name}` : `Web Design in ${r.name}`}
                      </Link>
                    ))}
                    <Link to="/web-design" className="border border-primary/40 text-primary px-4 py-2.5 text-sm hover:bg-primary/5 transition-colors" data-testid="market-all-link">
                      All markets →
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </section>
        );
      })()}

      <MarketCTA label={isIndustry ? place.toLowerCase() : place} />
    </div>
  );
}

/** Combo page: /web-design/:city/:industry */
function Combo({ combo }) {
  const [cfg, setCfg] = useState(null);
  const { fmt } = useCurrency();
  useEffect(() => { getPricingConfig().then(setCfg).catch(() => {}); }, []);
  const { city, industry: ind } = combo;
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: combo.title,
    provider: { "@type": "Person", name: "Julius Mason", url: "https://juliusmason.com" },
    areaServed: { "@type": "City", name: city.name },
    description: combo.description,
    offers: { "@type": "Offer", priceCurrency: "USD", price: "499", description: "Websites starting at $499" },
  };

  return (
    <div data-testid={`combo-page-${combo.slug.replace("/", "-")}`}>
      <SEO
        title={`${combo.title} | Julius Mason`}
        description={combo.description}
        path={`/web-design/${combo.slug}`}
        lang="en"
        keywords={`web design for ${ind.name.toLowerCase()} ${city.name}, ${ind.name.toLowerCase()} website ${city.name}, ${city.name} web designer`}
        schema={[
          serviceSchema,
          faqSchema(combo.faqs),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Web Design", path: "/web-design" },
            { name: city.name, path: `/web-design/${city.slug}` },
            { name: ind.name, path: `/web-design/${combo.slug}` },
          ]),
        ]}
      />

      <section className="pt-36 md:pt-44 pb-16 md:pb-20 border-b border-border">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <nav className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6" aria-label="Breadcrumb" data-testid="combo-breadcrumb">
            <Link to="/" className="hover:text-primary">Home</Link> / <Link to="/web-design" className="hover:text-primary">Web Design</Link> / <Link to={`/web-design/${city.slug}`} className="hover:text-primary">{city.name}</Link> / <span className="text-foreground">{ind.name}</span>
          </nav>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] max-w-4xl" data-testid="combo-h1">{combo.title}</h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">{combo.intro}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/pricing" data-testid="combo-quote-cta" className="inline-flex items-center gap-2 bg-primary text-white px-7 py-3.5 font-semibold hover:bg-primary/90 active:scale-95 transition">
              Get My Instant Quote <Zap className="w-4 h-4" />
            </Link>
            <Link to="/start-project" data-testid="combo-start-cta" className="inline-flex items-center gap-2 border border-border px-7 py-3.5 font-medium hover:border-primary hover:text-primary active:scale-95 transition">
              Start My Project <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="mt-5 font-mono text-xs uppercase tracking-widest text-muted-foreground">
            Remote service · Serving {city.name} businesses · Transparent pricing
          </p>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid lg:grid-cols-2 gap-14">
          <div>
            <SectionTitle overline={`${ind.name} in ${city.name}`} title={`What ${ind.name.toLowerCase()} in ${city.name} need online.`} />
            <ul className="space-y-4 -mt-4">
              {combo.needs.map((n, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <li className="flex gap-3 text-muted-foreground leading-relaxed" data-testid={`combo-need-${i}`}>
                    <Check className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" /> {n}
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
          <div>
            <SectionTitle overline="Typical Projects" title={`What I build for ${city.name} ${ind.name.toLowerCase()}.`} />
            <div className="space-y-3 -mt-4">
              {combo.projects.map((p, i) => (
                <div key={i} className="border border-border bg-card px-5 py-4 font-medium" data-testid={`combo-project-${i}`}>{p}</div>
              ))}
            </div>
            {cfg && (
              <p className="mt-6 text-sm text-muted-foreground" data-testid="combo-pricing-note">
                Transparent starting points: one-page sites from <b className="text-primary">{fmt(cfg.packages[0].price_from)}</b>, business websites
                from <b className="text-primary">{fmt(cfg.packages[1].price_from)}</b>, advanced builds from <b className="text-primary">{fmt(cfg.packages[2].price_from)}</b>.{" "}
                <Link to="/pricing" className="text-primary link-underline">Full pricing →</Link>
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 border-y border-border bg-secondary/20">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <SectionTitle overline="FAQ" title="Straight answers." />
          </div>
          <div className="lg:col-span-8">
            {combo.faqs.map((f, i) => (
              <details key={i} className="group border-b border-border py-5" data-testid={`combo-faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer font-medium text-lg list-none">
                  {f.q}
                  <ArrowRight className="w-4 h-4 text-primary rotate-90 group-open:-rotate-90 transition-transform flex-shrink-0 ml-4" />
                </summary>
                <p className="mt-4 text-muted-foreground leading-relaxed pr-8">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Sibling combos + parent pages */}
      <section className="py-16 border-b border-border">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 space-y-8">
          {combo.siblings.length > 0 && (
            <div>
              <p className="overline text-primary mb-6">More in {city.name}</p>
              <div className="flex flex-wrap gap-3" data-testid="combo-siblings">
                {combo.siblings.map((is) => (
                  <Link key={is} to={`/web-design/${city.slug}/${is}`} data-testid={`combo-sibling-${is}`}
                    className="border border-primary/40 text-primary px-4 py-2.5 text-sm hover:bg-primary/5 transition-colors">
                    {getMarket(is)?.name} in {city.name}
                  </Link>
                ))}
                <Link to={`/web-design/${city.slug}`} className="border border-border px-4 py-2.5 text-sm hover:border-primary hover:text-primary transition-colors">
                  All web design in {city.name} →
                </Link>
              </div>
            </div>
          )}
          <div>
            <p className="overline text-primary mb-6">{ind.name} elsewhere</p>
            <div className="flex flex-wrap gap-3" data-testid="combo-cities">
              {combosForIndustry(ind.slug).filter((cs) => cs !== city.slug).slice(0, 8).map((cs) => (
                <Link key={cs} to={`/web-design/${cs}/${ind.slug}`}
                  className="border border-border px-4 py-2.5 text-sm hover:border-primary hover:text-primary transition-colors">
                  {ind.name} in {getMarket(cs)?.name}
                </Link>
              ))}
              <Link to={`/web-design/${ind.slug}`} className="border border-border px-4 py-2.5 text-sm hover:border-primary hover:text-primary transition-colors">
                Web Design for {ind.name} →
              </Link>
            </div>
          </div>
        </div>
      </section>

      <MarketCTA label={`${ind.name.toLowerCase()} in ${city.name}`} />
    </div>
  );
}

export default function WebDesignLanding() {
  const { slug, sub } = useParams();
  if (!slug) return <Hub />;
  if (sub) {
    const combo = getCombo(slug, sub);
    if (!combo) {
      return (
        <div className="pt-40 pb-40 text-center" data-testid="combo-not-found">
          <p className="font-display text-4xl">Page not found.</p>
          <Link to="/web-design" className="text-primary mt-4 inline-block link-underline">Browse all markets</Link>
        </div>
      );
    }
    return <Combo key={combo.slug} combo={combo} />;
  }
  const market = getMarket(slug);
  if (!market) {
    return (
      <div className="pt-40 pb-40 text-center" data-testid="market-not-found">
        <p className="font-display text-4xl">Market not found.</p>
        <Link to="/web-design" className="text-primary mt-4 inline-block link-underline">Browse all markets</Link>
      </div>
    );
  }
  return <Market market={market} />;
}
